import apiClient from '../axios.js';

/**
 * 簽到活動管理 API
 */
export const checkinService = {
  /**
   * 獲取簽到配置列表
   * @param {Object} params - 查詢參數
   * @returns {Promise<Object>} 簽到配置列表
   */
  getConfigs: async (params = {}) => {
    const response = await apiClient.get('/admin/checkin/configs', { params });
    return response;
  },

  /**
   * 獲取單一簽到配置詳情
   * @param {string} id - 配置 ID
   * @returns {Promise<Object>} 簽到配置詳情
   */
  getConfig: async (id) => {
    const response = await apiClient.get(`/admin/checkin/configs/${id}`);
    return response;
  },

  /**
   * 獲取單一簽到配置詳情（別名，用於編輯頁面）
   * @param {string} id - 配置 ID
   * @returns {Promise<Object>} 簽到配置詳情
   */
  getConfigById: async (id) => {
    const response = await apiClient.get(`/admin/checkin/configs/${id}`);
    return response;
  },

  /**
   * 建立簽到配置
   * @param {Object} configData - 配置資料
   * @returns {Promise<Object>} 建立的配置
   */
  createConfig: async (configData) => {
    const response = await apiClient.post('/admin/checkin/configs', configData);
    return response;
  },

  /**
   * 更新簽到配置
   * @param {string} id - 配置 ID
   * @param {Object} configData - 更新資料
   * @returns {Promise<Object>} 更新的配置
   */
  updateConfig: async (id, configData) => {
    const response = await apiClient.put(`/admin/checkin/configs/${id}`, configData);
    return response;
  },

  /**
   * 刪除簽到配置
   * @param {string} id - 配置 ID
   * @returns {Promise<Object>} 刪除結果
   */
  deleteConfig: async (id) => {
    const response = await apiClient.delete(`/admin/checkin/configs/${id}`);
    return response;
  },

  /**
   * 獲取簽到獎勵列表
   * @param {string} configId - 配置 ID
   * @returns {Promise<Array>} 獎勵列表
   */
  getRewards: async (configId) => {
    const response = await apiClient.get(`/admin/checkin/configs/${configId}/rewards`);
    return response;
  },

  /**
   * 建立簽到獎勵
   * @param {string} configId - 配置 ID
   * @param {Object} rewardData - 獎勵資料
   * @returns {Promise<Object>} 建立的獎勵
   */
  createReward: async (configId, rewardData) => {
    const response = await apiClient.post(`/admin/checkin/configs/${configId}/rewards`, rewardData);
    return response;
  },

  /**
   * 更新簽到獎勵
   * @param {string} rewardId - 獎勵 ID
   * @param {Object} rewardData - 更新資料
   * @returns {Promise<Object>} 更新的獎勵
   */
  updateReward: async (rewardId, rewardData) => {
    const response = await apiClient.put(`/admin/checkin/rewards/${rewardId}`, rewardData);
    return response;
  },

  /**
   * 刪除簽到獎勵
   * @param {string} rewardId - 獎勵 ID
   * @returns {Promise<Object>} 刪除結果
   */
  deleteReward: async (rewardId) => {
    const response = await apiClient.delete(`/admin/checkin/rewards/${rewardId}`);
    return response;
  },

  /**
   * 獲取簽到記錄列表
   * @param {Object} params - 查詢參數
   * @returns {Promise<Object>} 簽到記錄列表
   */
  getRecords: async (params = {}) => {
    const response = await apiClient.get('/admin/checkin/records', { params });
    return response;
  },

  /**
   * 刪除簽到記錄
   * @param {string} recordId - 記錄 ID
   * @returns {Promise<Object>} 刪除結果
   */
  deleteRecord: async (recordId) => {
    const response = await apiClient.delete(`/admin/checkin/records/${recordId}`);
    return response;
  },

  /**
   * 獲取簽到統計
   * @param {string} configId - 配置 ID
   * @param {Object} params - 查詢參數
   * @returns {Promise<Object>} 統計資料
   */
  getStatistics: async (configId, params = {}) => {
    const response = await apiClient.get(`/admin/checkin/configs/${configId}/stats`, { params });
    return response;
  },

  /**
   * 取得視覺配置
   * @param {string} configId - 配置 ID
   * @returns {Promise<Object>} 視覺配置
   */
  getVisualConfig: async (configId) => {
    const response = await apiClient.get(`/admin/checkin/configs/${configId}/visual-config`);
    return response;
  },

  /**
   * 更新視覺配置
   * @param {string} configId - 配置 ID
   * @param {Object} visualConfig - 視覺配置
   * @returns {Promise<Object>} 更新結果
   */
  updateVisualConfig: async (configId, visualConfig) => {
    const response = await apiClient.put(`/admin/checkin/configs/${configId}/visual-config`, {
      visualConfig
    });
    return response;
  },

  /**
   * 上傳圖片
   * @param {string} configId - 配置 ID
   * @param {File} file - 圖片文件
   * @returns {Promise<Object>} 上傳結果
   */
  uploadImage: async (configId, file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post(
      `/admin/checkin/configs/${configId}/upload-image`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return response;
  },

  /**
   * 刪除圖片
   * @param {string} configId - 配置 ID
   * @param {string} fileUrl - 圖片 URL
   * @returns {Promise<Object>} 刪除結果
   */
  deleteImage: async (configId, fileUrl) => {
    const response = await apiClient.post(`/admin/checkin/configs/${configId}/delete-image`, {
      fileUrl
    });
    return response;
  }
};
